import { t } from 'i18next';
import { MythicGroupCharterLimits } from '../../../common/group';
import { getGuildLadderRungForPoints, GUILD_LADDER_POINTS_BY_TIER_SLUG } from '../../../common/guildPerkLadder';
import type { TSubscriptionsAppGame } from './userSubscriptionsLinks';

/**
 * Single source of truth for the marketing perk bullets shown on the premium tier cards.
 *
 * Consumed by both checkout flows so they can never drift apart:
 * - shared/frontend/components/patreon/detailedPatreonPlug.tsx (Patreon flow)
 * - shared/frontend/components/subscriptions/xsollaSubscriptionOptions.tsx (Xsolla flow)
 *
 * Perk display text lives in shared/locales/en/patreon.json under `perks.*`.
 * Every bullet must describe a real, code-backed entitlement — see
 * wowretail/src/backend/services/subscriptionServiceFactory.ts for the grant table.
 */
export type TPremiumTierPerk = {
  key: string;
  icon?: string;
  values?: Record<string, unknown>;
  games?: TSubscriptionsAppGame[];
  /**
   * i18n key of a rank name (patreon:ranks.*) resolved into `values.rankName`
   * at render time. Kept as a key (not a baked t() call) so translation happens
   * after i18n is initialized and in the viewer's locale.
   */
  rankNameKey?: string;
  /** Same mechanism for the guild ladder name (patreon:guild-ladder.name) into `values.ladderName`. */
  ladderNameKey?: string;
};

export type TPremiumTierSlug = 'revered' | 'exalted' | 'illustrious';

/** The tier that gets the "Most Popular" badge in both checkout flows. */
export const MOST_POPULAR_TIER: TPremiumTierSlug = 'exalted';

/** Resolves a perk's Trans values, translating `rankNameKey`/`ladderNameKey` at render time. */
export function resolvePerkValues(perk: TPremiumTierPerk): Record<string, unknown> | undefined {
  if (!perk.rankNameKey && !perk.ladderNameKey) {
    return perk.values;
  }

  const values = { ...perk.values };
  if (perk.rankNameKey) {
    values.rankName = t(perk.rankNameKey);
  }
  if (perk.ladderNameKey) {
    values.ladderName = t(perk.ladderNameKey);
  }
  return values;
}

export const PREMIUM_TIER_PERKS: Record<TPremiumTierSlug, TPremiumTierPerk[]> = {
  revered: [
    { key: 'patreon:perks.alts-visible-in-game', icon: 'text-premium-special fa fa-fw fa-people-roof' },
    { key: 'patreon:perks.score-display-control', icon: 'text-premium-special fa fa-fw fa-sliders' },
    { key: 'patreon:perks.express-queues', icon: 'text-premium-special fa fa-fw fa-forward' },
    { key: 'patreon:perks.browse-ad-free', icon: 'text-premium-special fa fa-fw fa-billboard' },
    { key: 'patreon:perks.headers-and-vanity', icon: 'text-premium-special fa fa-fw fa-address-card' },
    { key: 'patreon:perks.early-access', icon: 'text-premium-special fa fa-fw fa-calendar-check' },
    {
      key: 'patreon:perks.guild-ladder-revered',
      ladderNameKey: 'patreon:guild-ladder.name',
      // the rung one Revered sub reaches by itself — derived so the copy can't drift from the ladder
      values: { rung: getGuildLadderRungForPoints(GUILD_LADDER_POINTS_BY_TIER_SLUG.revered)!.rung },
      icon: 'text-premium-special fa fa-fw fa-users',
      games: ['wowretail']
    },
    {
      key: 'patreon:perks.rank-and-support-in-discord',
      rankNameKey: 'patreon:ranks.revered',
      icon: 'text-premium-special fa-brands fa-fw fa-discord'
    }
  ],
  exalted: [
    { key: 'patreon:perks.rewards-from-tier', rankNameKey: 'patreon:ranks.revered' },
    { key: 'patreon:perks.account-rescan', icon: 'text-premium-special fa fa-fw fa-arrows-rotate' },
    { key: 'patreon:perks.unlock-all-headers', icon: 'text-premium-special fa fa-fw fa-unlock' },
    {
      key: 'patreon:perks.express-queue-priority',
      values: { plusses: '+' },
      icon: 'text-premium-special fa fa-fw fa-forward'
    },
    {
      key: 'patreon:perks.vanity-url',
      values: { count: 2 },
      icon: 'text-premium-special fa fa-fw fa-diamond-turn-right'
    },
    {
      key: 'patreon:perks.guild-ladder-exalted',
      ladderNameKey: 'patreon:guild-ladder.name',
      // the rung one Exalted sub reaches by itself — derived so the copy can't drift from the ladder
      values: { rung: getGuildLadderRungForPoints(GUILD_LADDER_POINTS_BY_TIER_SLUG.exalted)!.rung },
      icon: 'text-premium-special fa fa-fw fa-users',
      games: ['wowretail']
    },
    {
      key: 'patreon:perks.rank-and-support-in-discord',
      rankNameKey: 'patreon:ranks.exalted',
      icon: 'text-premium-special fa-brands fa-fw fa-discord'
    }
  ],
  illustrious: [
    { key: 'patreon:perks.rewards-from-tier', rankNameKey: 'patreon:ranks.exalted' },
    { key: 'patreon:perks.auto-scan', icon: 'text-premium-special fa fa-fw fa-robot' },
    {
      key: 'patreon:perks.guild-ladder-illustrious',
      ladderNameKey: 'patreon:guild-ladder.name',
      // the rung one Illustrious sub reaches by itself — derived so the copy can't drift from the ladder
      values: { rung: getGuildLadderRungForPoints(GUILD_LADDER_POINTS_BY_TIER_SLUG.illustrious)!.rung },
      icon: 'text-premium-special fa fa-fw fa-users',
      games: ['wowretail']
    },
    { key: 'patreon:perks.animated-banners', icon: 'text-premium-special fa fa-fw fa-sparkles' },
    {
      key: 'patreon:perks.express-queue-priority',
      values: { plusses: '++' },
      icon: 'text-premium-special fa fa-fw fa-forward'
    },
    {
      key: 'patreon:perks.vanity-and-charters',
      values: { urlCount: 4, teamCount: MythicGroupCharterLimits.Patron_Illustrious },
      icon: 'text-premium-special fa fa-fw fa-diamond-turn-right'
    },
    {
      key: 'patreon:perks.rank-and-support-in-discord',
      rankNameKey: 'patreon:ranks.illustrious',
      icon: 'text-premium-special fa-brands fa-fw fa-discord'
    }
  ]
};

export function getPremiumTierPerks(tier: TPremiumTierSlug, game: TSubscriptionsAppGame): TPremiumTierPerk[] {
  return PREMIUM_TIER_PERKS[tier].filter((perk) => !perk.games || perk.games.includes(game));
}
